import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useResetScrollOnStepChange } from '@/hooks/useResetScrollOnStepChange';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ProgressIndicator } from '../CampaignCreation/ProgressIndicator';
import { SelectMetaAccountStep } from './steps/SelectMetaAccountStep';
import { MetaObjectiveStep } from './steps/MetaObjectiveStep';
import { MetaBudgetStep, type MetaBudgetMode } from './steps/MetaBudgetStep';
import { MetaScheduleStep } from './steps/MetaScheduleStep';
import { MetaSpecialAdCategoriesStep } from './steps/MetaSpecialAdCategoriesStep';
import { MetaReviewStep } from './steps/MetaReviewStep';
import {
  metaAdsApi,
  type CreateMetaCampaignRequest,
  type MetaBidStrategy,
  type MetaCampaignCreateStatus,
  type MetaCampaignObjective,
  type MetaSpecialAdCategory,
} from '@/services/api/metaAds';
import { debug } from '@/utils/debug';
import { formatDateForInput } from '@/utils/format';
import { appendOptimisticMetaCampaign } from '@/utils/optimisticMetaCache';
import type { AppConfiguration } from '@/types/api';

/**
 * CreateMetaCampaignWizard — A4.3.1.
 *
 * Structural mirror of `CampaignCreation/CreateCampaignWizard.tsx` (the
 * Google equivalent): same modal frame, same wizard state machine
 * (`currentStep`/`formData`/`errors`/`isSubmitting`/`submitError`), same
 * validate-step + handleNext + handlePrevious + handleSubmit flow, same
 * `isOpen` / `onClose` / `onSuccess` props. Reuses the shared
 * `ProgressIndicator`.
 *
 * Diverges at the per-step content: Meta semantics are different from
 * Google (ODAX objectives, special_ad_categories required-explicit, dollars
 * not micros). The divergence boundary is `./steps/`; everything above
 * (modal, state machine, validation flow) is platform-agnostic.
 */

interface CreateMetaCampaignWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  /**
   * When set, the wizard initializes with this Meta account pre-selected and
   * starts at Step 2 — Step 1's `SelectMetaAccountStep` never renders. The
   * Account page's launcher passes this after the user picks from
   * `OmniAccountPicker`. Unset = wizard falls back to its standalone behavior
   * (auto-skip when exactly 1 linked Meta account, render Step 1 otherwise).
   */
  preSelectedAccountId?: string | null;
}

interface FormErrors {
  account?: string;
  name?: string;
  objective?: string;
  amount?: string;
  startDate?: string;
  stopDate?: string;
  categories?: string;
}

const WIZARD_STEPS = [
  { label: 'Account', description: '' },
  { label: 'Basics', description: '' },
  { label: 'Budget', description: '' },
  { label: 'Schedule', description: '' },
  { label: 'Policy', description: '' },
  { label: 'Review', description: '' },
];

// The wizard skips Step 1 (account picker) when only one Meta account is
// linked. The skip happens in handleNext/handlePrevious; the rendered count
// is unchanged so ProgressIndicator's totalSteps stays honest.
//
// Name validator rejects C0 + DEL control chars (0x00–0x1F, 0x7F) — newlines,
// tabs, null bytes would round-trip into Meta and either get stripped or
// return an uninformative validation error. Mirrors the backend regex.
// eslint-disable-next-line no-control-regex
const NAME_PATTERN = /^[^\x00-\x1f\x7f]+$/;

export const CreateMetaCampaignWizard: React.FC<CreateMetaCampaignWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preSelectedAccountId = null,
}) => {
  const { linkedMetaAccountDetails } = useAuthStore();
  const memberUuid = useAuthStore((s) => s.memberUuid);
  const queryClient = useQueryClient();

  // Today as YYYY-MM-DD in the user's local timezone. Used both as the
  // schedule step's `<input type="date">` `min` attribute AND as the
  // start_time prefill (parity with Google's wizard at
  // `CampaignCreation/CreateCampaignWizard.tsx:93` — same helper). Using
  // local date parts (not `toISOString()`) avoids off-by-one for users
  // west of UTC who'd otherwise see tomorrow's date in the evening.
  //
  // Memoized per open/close cycle (not per render) so a user keeping the
  // wizard open across midnight doesn't trip the reset-on-open effect's
  // dependency on `today` and wipe their in-progress edits. `isOpen` is
  // intentionally a recompute trigger here, not a closed-over value —
  // exhaustive-deps doesn't grok that intent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const today = useMemo(() => formatDateForInput(new Date()), [isOpen]);

  const [currentStep, setCurrentStep] = useState(1);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  useResetScrollOnStepChange(currentStep, scrollBodyRef);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Form state. Initial budget mode is 'daily' (simpler default — no
  // stop-date requirement). Initial categories is an empty array; the
  // step requires an explicit selection before next-step. Status defaults
  // to PAUSED and bid strategy to LOWEST_COST_WITHOUT_CAP — same defaults
  // as the backend Pydantic model.
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [campaignName, setCampaignName] = useState('');
  // Pre-selected objective matches Google's `campaignType: 'SEARCH'` default
  // — Traffic is the most generic Meta objective ("drive site visits"),
  // analogous to Google Search's catch-all "people are actively looking for
  // this" framing. User can change in Step 2.
  const [objective, setObjective] = useState<MetaCampaignObjective>(
    'OUTCOME_TRAFFIC',
  );
  const [status, setStatus] = useState<MetaCampaignCreateStatus>('PAUSED');
  const [bidStrategy, setBidStrategy] = useState<MetaBidStrategy>(
    'LOWEST_COST_WITHOUT_CAP',
  );
  const [budgetMode, setBudgetMode] = useState<MetaBudgetMode>('daily');
  const [budgetAmount, setBudgetAmount] = useState('');
  // Prefill startDate with today's YYYY-MM-DD so the schedule step opens to
  // a sensible default; user can change it. Matches Google's wizard
  // (`formData.startDate: today`).
  const [startDate, setStartDate] = useState(today);
  const [stopDate, setStopDate] = useState('');
  const [hasEndDate, setHasEndDate] = useState(false);
  // Pre-select ['NONE'] — analogous to Google's
  // `containsEuPoliticalAdvertising: false` default (unchecked = "this is
  // NOT a regulated category"). User explicitly re-picks if their campaign
  // IS housing/employment/credit/political — same risk profile as Google's
  // unchecked-by-default EU political checkbox. The Review page's
  // "Before You Create" notice surfaces the declaration so a careless
  // operator still sees what's about to ship.
  const [specialAdCategories, setSpecialAdCategories] = useState<MetaSpecialAdCategory[]>([
    'NONE',
  ]);

  // Auto-select if exactly one Meta account linked OR the launcher passed a
  // preSelectedAccountId. preSelectedAccountId takes precedence over the
  // single-account fallback so a different-account pre-selection (e.g. user
  // has 2 Meta accounts and picked one from OmniAccountPicker) wins.
  useEffect(() => {
    const resolvedId =
      preSelectedAccountId ??
      (linkedMetaAccountDetails.length === 1
        ? linkedMetaAccountDetails[0].id
        : null);
    if (resolvedId && !selectedAccountId) {
      setSelectedAccountId(resolvedId);
    }
    // selectedAccountId intentionally omitted — auto-select runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedMetaAccountDetails, preSelectedAccountId]);

  // Reset state on open. When preSelectedAccountId is set OR there's exactly
  // 1 linked Meta account, Step 1 is bypassed (currentStep starts at 2).
  useEffect(() => {
    if (isOpen) {
      const resolvedId =
        preSelectedAccountId ??
        (linkedMetaAccountDetails.length === 1
          ? linkedMetaAccountDetails[0].id
          : null);
      setCurrentStep(resolvedId !== null ? 2 : 1);
      setIsSubmitting(false);
      setSubmitError(null);
      setErrors({});
      setSelectedAccountId(resolvedId);
      setCampaignName('');
      setObjective('OUTCOME_TRAFFIC');
      setStatus('PAUSED');
      setBidStrategy('LOWEST_COST_WITHOUT_CAP');
      setBudgetMode('daily');
      setBudgetAmount('');
      setStartDate(today);
      setStopDate('');
      setHasEndDate(false);
      setSpecialAdCategories(['NONE']);
    }
  }, [isOpen, linkedMetaAccountDetails, preSelectedAccountId, today]);

  // Switching budget mode clears the inactive scenario's stop_date
  // requirement signal — easier than tracking which was the last-set value.
  const handleChangeBudgetMode = (mode: MetaBudgetMode) => {
    setBudgetMode(mode);
    // Don't auto-clear stop date when going lifetime→daily — the user may
    // want to keep their date. The validator only requires it when lifetime.
  };

  // Confirm-on-cancel only when the user has touched at least one field.
  // Clean state (e.g. they opened the wizard and immediately clicked X)
  // skips the confirmation — no value in a "discard nothing?" prompt.
  // Defaulted fields (status, bidStrategy, budgetMode, startDate=today,
  // objective=OUTCOME_TRAFFIC, specialAdCategories=['NONE']) are excluded
  // — the user keeping the default doesn't count as "touched". hasEndDate
  // Includes every field a user can intentionally change away from defaults,
  // so closing mid-flow always prompts the discard confirm when there's any
  // editing to lose. Categories default is `['NONE']` (the policy step's
  // explicit no-regulated-category answer). Account dirty when the resolved
  // initial id (preselected or single-account auto-pick) differs from what
  // the user landed on.
  const categoriesAreDefault =
    specialAdCategories.length === 1 && specialAdCategories[0] === 'NONE';
  const initialAccountId =
    preSelectedAccountId ??
    (linkedMetaAccountDetails.length === 1
      ? linkedMetaAccountDetails[0].id
      : null);
  const isDirty =
    selectedAccountId !== initialAccountId ||
    campaignName !== '' ||
    objective !== 'OUTCOME_TRAFFIC' ||
    status !== 'PAUSED' ||
    bidStrategy !== 'LOWEST_COST_WITHOUT_CAP' ||
    budgetMode !== 'daily' ||
    budgetAmount !== '' ||
    startDate !== today ||
    stopDate !== '' ||
    hasEndDate !== false ||
    !categoriesAreDefault;

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 1) {
      if (!selectedAccountId) {
        newErrors.account = 'Pick a Meta ad account to continue.';
      }
    }

    if (step === 2) {
      if (!campaignName.trim()) {
        newErrors.name = 'Campaign name is required.';
      } else if (campaignName.length > 400) {
        newErrors.name = 'Name must be 400 characters or fewer.';
      } else if (!NAME_PATTERN.test(campaignName)) {
        newErrors.name = 'Name cannot contain control characters (tabs, newlines).';
      }
      if (!objective) {
        newErrors.objective = 'Pick an objective.';
      }
    }

    if (step === 3) {
      const parsed = parseFloat(budgetAmount);
      if (!budgetAmount || Number.isNaN(parsed) || parsed <= 0) {
        newErrors.amount = 'Budget must be a positive amount.';
      }
    }

    if (step === 4) {
      // Start date is required (D7 strictness — parity with Google).
      if (!startDate) {
        newErrors.startDate = 'Start date is required.';
      } else if (startDate < today) {
        newErrors.startDate = 'Start date cannot be in the past.';
      }
      // Stop date rules depend on mode + toggle:
      // - Lifetime: always required
      // - Daily + hasEndDate: required
      // - Daily + !hasEndDate: not shown, not required
      const stopExpected = budgetMode === 'lifetime' || hasEndDate;
      if (stopExpected && !stopDate) {
        newErrors.stopDate =
          budgetMode === 'lifetime'
            ? 'Stop date is required when using a lifetime budget.'
            : 'Stop date is required when "Set an end date" is on.';
      }
      if (stopExpected && startDate && stopDate && stopDate <= startDate) {
        newErrors.stopDate = 'Stop date must be after the start date.';
      }
    }

    if (step === 5) {
      if (specialAdCategories.length === 0) {
        newErrors.categories =
          'Select either "None of the above" or one or more regulated categories. ' +
          'Skipping this is a Meta-policy violation.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length));
    }
  };

  // Floor for backward navigation. The Account step (Step 1) is one-way:
  // once the user advances past it, they can't go back to change the
  // account — picking the wrong one means closing the wizard and starting
  // over (analogous treatment in the Google wizard's `previousFloor`).
  // Changing the account mid-flow would invalidate every later step (budget
  // rules, available creative library, etc.) so this is the safe stance.
  const previousFloor = 2;

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, previousFloor));
  };

  const handleStepClick = (step: number) => {
    if (step < currentStep && step >= previousFloor) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = async () => {
    // Re-run every step's validation in case the user jumped past one via
    // the ProgressIndicator click-back.
    for (let step = 1; step <= 5; step++) {
      if (!validateStep(step)) {
        setCurrentStep(step);
        return;
      }
    }

    if (!selectedAccountId || !objective) {
      // Should never hit — validateStep above caught these. Type guard.
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload: CreateMetaCampaignRequest = {
        meta_ad_account_id: selectedAccountId,
        campaign_name: campaignName.trim(),
        objective,
        status,
        bid_strategy: bidStrategy,
        special_ad_categories: specialAdCategories,
        // start_time is required after D7 strictness; the validator above
        // already enforced non-empty + not-past, so it's safe to send raw.
        start_time: startDate,
      };
      const amount = parseFloat(budgetAmount);
      if (budgetMode === 'daily') {
        payload.daily_budget = amount;
      } else {
        payload.lifetime_budget = amount;
      }
      // Stop time is conditional on (lifetime mode) OR (daily mode + hasEndDate).
      // Omit when daily + !hasEndDate so Meta runs the campaign indefinitely.
      const stopExpected = budgetMode === 'lifetime' || hasEndDate;
      if (stopExpected && stopDate) {
        payload.stop_time = stopDate;
      }

      debug.log('CreateMetaCampaignWizard: Submitting', payload);
      const response = await metaAdsApi.createCampaign(payload);
      debug.log('CreateMetaCampaignWizard: Created', response);

      // Optimistic app-config splice — mirrors the ad-set / ad wizards.
      // Uses the server-authoritative `applied_entity` dict. See
      // docs/FRONTEND_MUTATION_PATTERNS.md for the "why".
      if (memberUuid && response.applied_entity) {
        // Backend passes through the raw AppCampaignConfig-shaped dict
        // (including platform_source, ad_sets, etc.). Cast at splice boundary.
        const appliedCampaign = response.applied_entity as unknown as import('@/types/api').AppCampaignConfig;
        queryClient.setQueryData<AppConfiguration>(
          ['app-config', memberUuid],
          (prev) => appendOptimisticMetaCampaign(prev, appliedCampaign),
        );
      }
      // Invalidate the /campaigns list so the account page picks up the
      // new campaign card without waiting for backend ingest. No refetch of
      // app-config — that's the race.
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });

      await onSuccess();
      onClose();
    } catch (error: unknown) {
      debug.error('CreateMetaCampaignWizard: Submit failed', error);
      let message = 'Failed to create campaign. Please try again.';
      const apiError = error as {
        response?: {
          data?: { detail?: string | unknown; message?: string };
        };
        message?: string;
      };
      if (apiError.response?.data) {
        const data = apiError.response.data;
        const detail = data.detail;
        if (typeof detail === 'string') {
          message = detail;
        } else if (detail) {
          message = JSON.stringify(detail);
        } else if (data.message) {
          message = data.message;
        }
      } else if (apiError.message) {
        message = apiError.message;
      }
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedAccount = linkedMetaAccountDetails.find(
    (acc) => acc.id === selectedAccountId,
  ) ?? null;

  // Step 1 is auto-skipped when only one Meta account is linked; the
  // ProgressIndicator still shows it as step 1 / completed so the chrome
  // is consistent with the multi-account path.
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create new Meta campaign"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-4 sm:my-8 relative">
        <button
          onClick={handleRequestClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close wizard"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 pr-8">
            Create New Meta Ads Campaign
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Set up a new Meta Ads campaign in {WIZARD_STEPS.length} simple steps
          </p>
        </div>

        <div className="px-4 sm:px-6 py-2 border-b border-gray-100">
          <ProgressIndicator
            currentStep={currentStep}
            totalSteps={WIZARD_STEPS.length}
            steps={WIZARD_STEPS}
            onStepClick={handleStepClick}
          />
        </div>

        <div
          ref={scrollBodyRef}
          className="px-6 py-6 overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}
        >
          {currentStep === 1 && (
            <SelectMetaAccountStep
              accounts={linkedMetaAccountDetails.map((acc) => ({
                id: acc.id,
                businessName: acc.businessName,
              }))}
              selectedAccountId={selectedAccountId}
              onSelectAccount={setSelectedAccountId}
              error={errors.account}
            />
          )}

          {currentStep === 2 && (
            <MetaObjectiveStep
              campaignName={campaignName}
              objective={objective}
              status={status}
              onChangeName={setCampaignName}
              onChangeObjective={setObjective}
              onChangeStatus={setStatus}
              errors={{ name: errors.name, objective: errors.objective }}
            />
          )}

          {currentStep === 3 && (
            <MetaBudgetStep
              budgetMode={budgetMode}
              budgetAmount={budgetAmount}
              bidStrategy={bidStrategy}
              onChangeMode={handleChangeBudgetMode}
              onChangeAmount={setBudgetAmount}
              onChangeBidStrategy={setBidStrategy}
              errors={{ amount: errors.amount }}
            />
          )}

          {currentStep === 4 && (
            <MetaScheduleStep
              budgetMode={budgetMode}
              startDate={startDate}
              stopDate={stopDate}
              hasEndDate={hasEndDate}
              today={today}
              onChangeStartDate={setStartDate}
              onChangeStopDate={setStopDate}
              onChangeHasEndDate={setHasEndDate}
              errors={{
                startDate: errors.startDate,
                stopDate: errors.stopDate,
              }}
            />
          )}

          {currentStep === 5 && (
            <MetaSpecialAdCategoriesStep
              categories={specialAdCategories}
              onChange={setSpecialAdCategories}
              errors={{ categories: errors.categories }}
            />
          )}

          {currentStep === 6 && (
            <MetaReviewStep
              accountId={selectedAccountId}
              accountBusinessName={selectedAccount?.businessName ?? null}
              campaignName={campaignName}
              objective={objective}
              status={status}
              bidStrategy={bidStrategy}
              budgetMode={budgetMode}
              budgetAmount={budgetAmount}
              startDate={startDate}
              stopDate={stopDate}
              hasEndDate={hasEndDate}
              specialAdCategories={specialAdCategories}
              onEditStep={handleStepClick}
            />
          )}

          {submitError && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p role="alert" className="text-sm text-red-600">
                {submitError}
              </p>
            </div>
          )}
        </div>

        {/* Desktop footer */}
        <div className="hidden sm:flex px-6 py-4 border-t border-gray-200 justify-between items-center">
          <Button
            onClick={handlePrevious}
            disabled={isSubmitting || currentStep <= previousFloor}
            variant="secondary"
          >
            Previous
          </Button>
          <div className="text-sm text-gray-500">
            Step {currentStep} of {WIZARD_STEPS.length}
          </div>
          {currentStep < WIZARD_STEPS.length ? (
            <Button onClick={handleNext} disabled={isSubmitting}>
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              variant="primary"
            >
              {isSubmitting ? 'Creating Campaign...' : 'Create Campaign'}
            </Button>
          )}
        </div>

        {/* Mobile footer (matches Google's wizard order) */}
        <div className="flex sm:hidden flex-col px-6 py-4 border-t border-gray-200 items-center gap-3">
          {currentStep < WIZARD_STEPS.length ? (
            <Button onClick={handleNext} disabled={isSubmitting} className="w-full">
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              variant="primary"
              className="w-full"
            >
              {isSubmitting ? 'Creating Campaign...' : 'Create Campaign'}
            </Button>
          )}
          <div className="text-sm text-gray-500">
            Step {currentStep} of {WIZARD_STEPS.length}
          </div>
          <Button
            onClick={handlePrevious}
            disabled={isSubmitting || currentStep <= previousFloor}
            variant="secondary"
            className="w-full"
          >
            Previous
          </Button>
        </div>
      </div>

      {/* Cancel-while-dirty confirmation. Mounted inside the wizard root so
          its z-index stacking sits above the wizard's own backdrop. */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => {
          setShowCancelConfirm(false);
          onClose();
        }}
        title="Discard campaign?"
        message="You have unsaved changes. Closing now will discard the campaign you've been setting up."
        confirmText="Discard"
        cancelText="Keep editing"
        variant="warning"
      />
    </div>
  );
};
